import React, { useCallback, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { useBoardTheme } from "./boardThemeStore";
import { repeaterToArray } from "./commercialProjectAcf";
import {
  PERMITS_REPEATER_FIELD,
  PERMIT_EDIT_CONTEXT,
  PERMITTING_ASSIGNMENT_FIELDS,
  PERMITTING_TRACKING_FIELDS,
  canManagePermitRows,
  canManagePermitRiders,
  permitRowFields,
  permitSubFieldDisabled,
  permitRiderFieldVisible,
  riderNeedsPermitPlan,
  flattenPermitsForGrid,
} from "./commercialPermitsSchema";
import { CommercialFieldInput } from "./CommercialProjectSectionPanel";
import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { mergeCommercialFileFieldsFromServer, applyPermitPlanMaterialsToDraft } from "./commercialProjectFiles";
import { readCommercialDomains } from "./commercialProjectAcf";
import { readExpansionEditDomains } from "./expansionProjectForms";
import { EXPANSION_SUMMARY_DOMAIN } from "./expansionProjectSchema";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import { canEditProjectDrafterAssignmentFromPermitting } from "./commercialRoleAccess";
import { useEngineeringMaterialsCatalog } from "./engineeringMaterialsCatalog";
import { CommercialFieldFileIcon } from "./CommercialFieldFileDisplay";
import PermitMaterialsReviewBanner from "./PermitMaterialsReviewBanner";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import PermitCoveragePanel from "./PermitCoveragePanel";
import "./permitsSection.css";

ModuleRegistry.registerModules([AllCommunityModule]);

function PermitsFileCell(params) {
  const value = params.value;
  if (!value) return null;
  return (
    <CommercialFieldFileIcon
      value={value}
      fieldKey={params.colDef?.field}
      accept=".pdf"
    />
  );
}

function RefundableDepositCell(params) {
  const checked = Boolean(params.value);
  return (
    <input
      type="checkbox"
      checked={checked}
      disabled
      readOnly
      aria-label="Refundable deposit"
      className="permits-grid-refundable-checkbox"
    />
  );
}

const PERMITS_GRID_COL_DEFS = [
  { colId: "line", field: "line", headerName: "Line", filter: true, minWidth: 120, flex: 1 },
  { colId: "agency", field: "agency", headerName: "Agency", filter: true, minWidth: 120, flex: 1 },
  { colId: "location", field: "location", headerName: "Location", filter: true, minWidth: 140, flex: 1 },
  { colId: "permit_number", field: "permit_number", headerName: "Permit #", filter: true, minWidth: 120, flex: 1 },
  { colId: "rowKind", field: "rowKind", headerName: "Type", filter: true, width: 90 },
  {
    colId: "refundable_deposit",
    field: "refundable_deposit",
    headerName: "Refundable deposit",
    width: 150,
    cellRenderer: RefundableDepositCell,
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
  },
  {
    colId: "deposit_amount",
    field: "deposit_amount",
    headerName: "Deposit amount",
    filter: true,
    width: 130,
  },
  {
    colId: "application_submitted_date",
    field: "application_submitted_date",
    headerName: "Applied",
    filter: true,
    width: 110,
    comparator: (_a, _b, nodeA, nodeB) =>
      String(nodeA?.data?.application_submitted_sort || "").localeCompare(
        String(nodeB?.data?.application_submitted_sort || "")
      ),
  },
  {
    colId: "permit_received_date",
    field: "permit_received_date",
    headerName: "Received",
    filter: true,
    width: 110,
    comparator: (_a, _b, nodeA, nodeB) =>
      String(nodeA?.data?.permit_received_sort || "").localeCompare(
        String(nodeB?.data?.permit_received_sort || "")
      ),
  },
  {
    colId: "permit_expiration_date",
    field: "permit_expiration_date",
    headerName: "Expires",
    filter: true,
    width: 110,
    comparator: (_a, _b, nodeA, nodeB) =>
      String(nodeA?.data?.permit_expiration_sort || "").localeCompare(
        String(nodeB?.data?.permit_expiration_sort || "")
      ),
  },
  { colId: "permit_plan", field: "permit_plan", headerName: "Plan", width: 72, cellRenderer: PermitsFileCell },
  {
    colId: "permit_application",
    field: "permit_application",
    headerName: "Application",
    width: 100,
    cellRenderer: PermitsFileCell,
  },
  { colId: "permit", field: "permit", headerName: "Permit", width: 80, cellRenderer: PermitsFileCell },
  { colId: "materials", field: "materials", headerName: "Materials", filter: true, minWidth: 120, flex: 1 },
];

function PermitsGrid({ permits }) {
  const { agGridTheme } = useBoardTheme();
  const rowData = useMemo(() => flattenPermitsForGrid(permits), [permits]);
  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, suppressMovable: false }),
    []
  );

  if (!rowData.length) {
    return <p className="project-detail-muted">No permits yet.</p>;
  }

  return (
    <div className="permits-ag-grid-wrap">
      <AgGridReact
        theme={agGridTheme}
        rowData={rowData}
        columnDefs={PERMITS_GRID_COL_DEFS}
        defaultColDef={defaultColDef}
        domLayout="autoHeight"
        getRowId={(p) => p.data.rowId}
        suppressCellFocus
        animateRows={false}
        rowClassRules={{
          "permits-grid-row-rider": (p) => p.data?.rowKind === "Supporting doc",
        }}
      />
    </div>
  );
}

function riderCardTitle(row, riderIdx, rowLabel) {
  const type = row?.rider_type != null ? String(row.rider_type).trim() : "";
  const permitNumber = row?.permit_number != null ? String(row.permit_number).trim() : "";
  const base = `${rowLabel || "Supporting document"} ${riderIdx + 1}`;
  let title = type ? `${base} — ${type}` : base;
  if (permitNumber) title += `${type ? " · " : " — "}${permitNumber}`;
  return title;
}

function PermitRidersEditor({
  permitIndex,
  riders,
  onRidersChange,
  editContext,
  ctx,
  pathPrefix,
  fileEditing,
  materialsCatalog,
}) {
  const rows = repeaterToArray(riders);
  const riderField = PERMITS_REPEATER_FIELD.fields.find((f) => f.key === "riders");
  if (!riderField) return null;

  const canManageRiders = canManagePermitRiders(editContext);
  if (!canManageRiders && !rows.length) return null;

  const visibleFieldsForRow = (row) =>
    (riderField.fields || []).filter((sub) => permitRiderFieldVisible(sub, editContext, row));

  return (
    <div
      className={
        editContext === PERMIT_EDIT_CONTEXT.PERMITTING
          ? "permits-riders-editor"
          : "permits-riders-engineering"
      }
    >
      <h5 className="permits-riders-title">{riderField.label}</h5>
      {rows.length === 0 ? <p className="project-detail-muted">No supporting documents yet.</p> : null}
      {rows.map((row, riderIdx) => {
        const visibleFields = visibleFieldsForRow(row);
        const showExtensionNote =
          editContext === PERMIT_EDIT_CONTEXT.ENGINEERING &&
          !riderNeedsPermitPlan(row?.rider_type);
        return (
          <div key={riderIdx} className="commercial-repeater-card permits-rider-card">
            <div className="commercial-repeater-card-head">
              <strong>{riderCardTitle(row, riderIdx, riderField.rowLabel)}</strong>
              {canManageRiders ? (
                <button
                  type="button"
                  className="email-triggers-button"
                  style={{ fontSize: 12, padding: "4px 8px" }}
                  onClick={() => onRidersChange(rows.filter((_, i) => i !== riderIdx))}
                >
                  Remove
                </button>
              ) : null}
            </div>
            {showExtensionNote ? (
              <p className="project-detail-muted permits-rider-extension-note">No supporting document required.</p>
            ) : null}
            {visibleFields.map((sub) => (
              <CommercialFieldInput
                key={sub.key}
                field={sub}
                value={row?.[sub.key]}
                onChange={(v) => {
                  const next = rows.map((r, i) => (i === riderIdx ? { ...r, [sub.key]: v } : r));
                  onRidersChange(next);
                }}
                disabled={permitSubFieldDisabled(sub, editContext)}
                ctx={ctx}
                pathPrefix={[...pathPrefix, permitIndex, "riders", riderIdx]}
                fileEditing={fileEditing}
                materialsCatalog={materialsCatalog}
              />
            ))}
          </div>
        );
      })}
      {canManageRiders ? (
        <button
          type="button"
          className="email-triggers-button email-triggers-button-primary"
          style={{ fontSize: 13 }}
          onClick={() => onRidersChange([...rows, {}])}
        >
          {riderField.addButtonLabel || `Add ${riderField.rowLabel || "supporting document"}`}
        </button>
      ) : null}
    </div>
  );
}

function PermitsEditor({
  permits,
  onChange,
  editContext,
  ctx,
  pathPrefix,
  fileEditing,
  materialsCatalog,
}) {
  const rows = repeaterToArray(permits);
  const canAddPermits = canManagePermitRows(editContext);

  const updatePermit = useCallback(
    (index, patch) => {
      onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    },
    [onChange, rows]
  );

  const updatePermitField = useCallback(
    (index, key, value) => {
      onChange(rows.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
    },
    [onChange, rows]
  );

  return (
    <div className="commercial-repeater">
      {rows.length === 0 ? <p className="project-detail-muted">No permits yet.</p> : null}
      {rows.map((row, rowIdx) => (
        <div key={rowIdx} className="commercial-repeater-card">
          <div className="commercial-repeater-card-head">
            <strong>
              {PERMITS_REPEATER_FIELD.rowLabel || "Permit"} {rowIdx + 1}
              {row?.agency ? ` — ${row.agency}` : ""}
              {row?.permit_number
                ? `${row?.agency ? " · " : " — "}${row.permit_number}`
                : ""}
            </strong>
            {canAddPermits ? (
              <button
                type="button"
                className="email-triggers-button"
                style={{ fontSize: 12, padding: "4px 8px" }}
                onClick={() => onChange(rows.filter((_, i) => i !== rowIdx))}
              >
                Remove
              </button>
            ) : null}
          </div>
          <PermitMaterialsReviewBanner
            permitRow={row}
            onMarkVerified={() => updatePermit(rowIdx, { materials_review_pending: false })}
            enabled={isPluginEnabled(PLUGIN_IDS.PERMIT_PLAN_MATERIAL_EXTRACTION)}
          />
          {permitRowFields
            .filter((sub) => sub.key !== "riders")
            .map((sub) => (
              <CommercialFieldInput
                key={sub.key}
                field={sub}
                value={row?.[sub.key]}
                onChange={(v) => updatePermitField(rowIdx, sub.key, v)}
                disabled={permitSubFieldDisabled(sub, editContext)}
                ctx={ctx}
                pathPrefix={[...pathPrefix, rowIdx]}
                fileEditing={fileEditing}
                materialsCatalog={materialsCatalog}
              />
            ))}
          <PermitRidersEditor
            permitIndex={rowIdx}
            riders={row?.riders}
            onRidersChange={(nextRiders) => updatePermit(rowIdx, { riders: nextRiders })}
            editContext={editContext}
            ctx={ctx}
            pathPrefix={pathPrefix}
            fileEditing={fileEditing}
            materialsCatalog={materialsCatalog}
          />
        </div>
      ))}
      {canAddPermits ? (
        <button
          type="button"
          className="email-triggers-button email-triggers-button-primary"
          style={{ fontSize: 13 }}
          onClick={() => onChange([...rows, {}])}
        >
          Add {PERMITS_REPEATER_FIELD.rowLabel || "permit"}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Permits section — AG Grid read view; context-aware edit (engineering vs permitting).
 */
export default function PermitsSectionPanel({
  permits,
  onChange,
  editing = false,
  editContext = PERMIT_EDIT_CONTEXT.PERMITTING,
  gridOnly = false,
  title = "Permits",
  ctx,
  pathPrefix = [],
  fileEditing = null,
  materialsCatalog = null,
}) {
  if (editing && typeof onChange === "function") {
    return (
      <section className="permits-section-panel">
        {title ? <h4 className="commercial-repeater-title">{title}</h4> : null}
        <PermitsEditor
          permits={permits}
          onChange={onChange}
          editContext={editContext}
          ctx={ctx}
          pathPrefix={pathPrefix}
          fileEditing={fileEditing}
          materialsCatalog={materialsCatalog}
        />
      </section>
    );
  }

  return (
    <section className="permits-section-panel">
      <h3 className="commercial-section-title">{title}</h3>
      <PermitsGrid permits={permits} />
    </section>
  );
}

/** Read-only engineering permits shown on the Construction read tab. */
export function ConstructionPermitsReadPanel({ permits, title = "Permits" }) {
  return (
    <section className="permits-section-panel">
      <h3 className="commercial-section-title">{title}</h3>
      <PermitsGrid permits={permits} />
    </section>
  );
}

/** View-only permit coverage for expansion Engineering / Construction tabs. */
export function ExpansionPermitCoverageReadPanel({
  projectId,
  permitCoverage = null,
  confirmationValue,
  apiUrl,
  getAuthHeaders,
}) {
  return (
    <PermitCoveragePanel
      projectId={projectId}
      coverage={permitCoverage}
      editing={false}
      apiUrl={apiUrl}
      getAuthHeaders={getAuthHeaders}
      confirmationValue={confirmationValue}
    />
  );
}

/** Landing-page Permitting tab — read-only grid or permitting-context editor. */
export function LandingPermittingTab({
  domains,
  draftDomains,
  onDomainsChange,
  editing = false,
  projectId,
  getAuthHeaders,
  onFileSynced,
  summaryDomainKey = COMMERCIAL_DOMAINS.SUMMARY,
  apiUrl,
  permitCoverage = null,
  onPermitCoverageChange,
}) {
  const engKey = COMMERCIAL_DOMAINS.ENGINEERING;
  const isExpansion = summaryDomainKey === EXPANSION_SUMMARY_DOMAIN;
  const activeDomains = editing && draftDomains ? draftDomains : domains;
  const engPayload = activeDomains?.[engKey] || {};
  const permits = engPayload?.permits;
  const confirmationValue = engPayload?.permit_coverage_confirmation;
  const viewerEmail = getViewerEmailFromStorage();
  const materialsCatalog = useEngineeringMaterialsCatalog();
  const ctx = useMemo(
    () => ({
      summary: activeDomains?.[summaryDomainKey] || {},
      networking: activeDomains?.[COMMERCIAL_DOMAINS.NETWORKING] || {},
      viewerEmail,
    }),
    [activeDomains, summaryDomainKey, viewerEmail]
  );

  const handleFileUpdated = useCallback(
    (uploadResult) => {
      if (uploadResult?.project?.acf_groups_json && onDomainsChange) {
        const server =
          summaryDomainKey === EXPANSION_SUMMARY_DOMAIN
            ? readExpansionEditDomains(uploadResult.project, uploadResult.project.acf_domains)
            : readCommercialDomains(uploadResult.project.acf_groups_json);
        onDomainsChange((prev) => {
          let next = mergeCommercialFileFieldsFromServer(prev || {}, server);
          const ppm = uploadResult?.permitPlanMaterials;
          if (
            isPluginEnabled(PLUGIN_IDS.PERMIT_PLAN_MATERIAL_EXTRACTION) &&
            ppm?.applied &&
            Array.isArray(ppm.materials)
          ) {
            next = applyPermitPlanMaterialsToDraft(next, engKey, ppm.permitIndex, ppm.materials);
          }
          return next;
        });
      }
      onFileSynced?.(uploadResult);
    },
    [onDomainsChange, onFileSynced, summaryDomainKey, engKey]
  );

  const fileEditing =
    editing && projectId && getAuthHeaders
      ? { projectId, getAuthHeaders, onFileUpdated: handleFileUpdated }
      : null;

  const setPermits = useCallback(
    (next) => {
      if (!onDomainsChange) return;
      onDomainsChange((prev) => ({
        ...prev,
        [engKey]: { ...(prev?.[engKey] || {}), permits: next },
      }));
    },
    [onDomainsChange, engKey]
  );

  const setTrackingField = useCallback(
    (key, value) => {
      if (!onDomainsChange) return;
      onDomainsChange((prev) => ({
        ...prev,
        [engKey]: { ...(prev?.[engKey] || {}), [key]: value },
      }));
    },
    [onDomainsChange, engKey]
  );

  return (
    <div className="commercial-tab-read-panel project-detail-tab-content">
      {isExpansion ? (
        <PermitCoveragePanel
          projectId={projectId}
          coverage={permitCoverage}
          onCoverageChange={onPermitCoverageChange}
          editing={editing}
          apiUrl={apiUrl}
          getAuthHeaders={getAuthHeaders}
          confirmationValue={confirmationValue}
          onConfirmationFileSynced={handleFileUpdated}
        />
      ) : null}
      <div className="commercial-section permits-assignment-fields">
        <h3 className="commercial-section-title">Assignment</h3>
        {PERMITTING_ASSIGNMENT_FIELDS.map((field) => {
          const canEditField = editing && canEditProjectDrafterAssignmentFromPermitting(viewerEmail);
          return (
            <CommercialFieldInput
              key={field.key}
              field={field}
              value={engPayload?.[field.key]}
              onChange={(v) => setTrackingField(field.key, v)}
              disabled={!canEditField}
              ctx={ctx}
              pathPrefix={[engKey, field.key]}
              fileEditing={null}
            />
          );
        })}
      </div>
      <div className="commercial-section permits-tracking-fields">
        <h3 className="commercial-section-title">Permit tracking</h3>
        {PERMITTING_TRACKING_FIELDS.map((field) => (
          <CommercialFieldInput
            key={field.key}
            field={field}
            value={engPayload?.[field.key]}
            onChange={(v) => setTrackingField(field.key, v)}
            disabled={!editing}
            ctx={ctx}
            pathPrefix={[engKey, field.key]}
            fileEditing={null}
          />
        ))}
      </div>
      <PermitsSectionPanel
        title="Permitting"
        permits={permits}
        onChange={setPermits}
        editing={editing}
        editContext={PERMIT_EDIT_CONTEXT.PERMITTING}
        gridOnly
        ctx={ctx}
        pathPrefix={[engKey, "permits"]}
        fileEditing={fileEditing}
        materialsCatalog={materialsCatalog}
      />
    </div>
  );
}
